import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, computed, input, InputSignal, OnInit } from '@angular/core';
import { ActivityPanelModes } from 'src/app/constants.new';
import { SharedPipesModule } from '../../../../UI-Lib/Pipes/shared-pipes.module';
import { ActivityService, PropCharNames } from '../../activity.service';

@Component({
    selector: 'activity-table',
    templateUrl: 'activityTable.component.html',
    styleUrls: ['activityTable.component.scss'],
    imports: [CommonModule, SharedPipesModule],
})
export class ActivityTableComponent implements OnInit {
    constructor(
        private readonly _activityService: ActivityService,
        private readonly _currencyPipe: CurrencyPipe,
    ) {}

    config: InputSignal<any> = input();
    originalConfig: InputSignal<any> = input();
    parcelId: InputSignal<number> = input();

    ActivityPanelModes = ActivityPanelModes;
    PropCharNames = PropCharNames;

    showingAllYears = computed(() => {
        return this.config().loadedAllYears;
    });

    ngOnInit(): void {}

    getCellValue(seriesItem, year: number, symbol: boolean, fractionSize: number) {
        const xAxisCategoryIndex = this.config().xAxis.categories.indexOf(year);
        const val = seriesItem.data[xAxisCategoryIndex].y;

        return val || val === 0
            ? this._currencyPipe.transform(val, 'USD', symbol ? '$' : '', `1.${fractionSize}-${fractionSize}`)
            : '';
    }

    isProjected(seriesItem, year: number): boolean {
        const xAxisCategoryIndex = this.config().xAxis.categories.indexOf(year);
        return seriesItem.data[xAxisCategoryIndex].isProjected;
    }

    getTotalForSingleYear(year: number): number {
        let result = 0;
        const xAxisCategoryIndex = this.config().xAxis.categories.indexOf(year);
        this.config().series.forEach((item) => {
            if (item.data[xAxisCategoryIndex].originalValue)
                result = new Decimal(result).plus(item.data[xAxisCategoryIndex].originalValue).toNumber();
        });

        return result;
    }

    isProjectedFmvForSingleYear(year: number): boolean {
        let result = false;
        const xAxisCategoryIndex = this.config().xAxis.categories.indexOf(year);
        this.config().series.forEach((item) => {
            result = result || item.data[xAxisCategoryIndex].isProjected;
        });

        return result;
    }

    getTotalFmvByPropValueForSingleYear(year: number, propCharName: string): number {
        const totalFmv = this.getTotalForSingleYear(year);

        if (
            propCharName == PropCharNames.SQFT &&
            (this.config().chartType == ActivityPanelModes.landFMVPerSqFt ||
                this.config().chartType == ActivityPanelModes.landFMVPerAcre)
        ) {
            // special case, instead of sqft attribute we will use acreage /43560
            //
            const i = this.config().propertyCharacteristics.findIndex((o) => o.label == PropCharNames.ACREAGE);
            if (i >= 0) {
                return new Decimal(totalFmv)
                    .dividedBy(parseFloat(this.config().propertyCharacteristics[i].value))
                    .dividedBy(43560)
                    .toNumber();
            }
        } else {
            const i = this.config().propertyCharacteristics.findIndex((o) => o.label == propCharName);
            if (i >= 0) {
                return new Decimal(totalFmv)
                    .dividedBy(parseFloat(this.config().propertyCharacteristics[i].value))
                    .toNumber();
            }
        }
        return -1;
    }

    getTotalColumnTitle(): string {
        switch (this.config().chartType) {
            case ActivityPanelModes.totalFMV:
            case ActivityPanelModes.totalFMVPerSqFt:
            case ActivityPanelModes.totalFMVPerUnit:
                return 'Total MV';
            case ActivityPanelModes.landFMVPerSqFt:
            case ActivityPanelModes.landFMVPerAcre:
                return 'Land MV';
            case ActivityPanelModes.totalTaxes:
                return 'Total Tax';
        }
    }

    showColumn(propCharName: string, arrayOfAllowedModes: any[]): boolean {
        if (arrayOfAllowedModes.indexOf(this.config().chartType) < 0) {
            return false;
        }

        if (propCharName === '') {
            return true;
        }

        const i = this.config().propertyCharacteristics.findIndex((o) => o.label == propCharName);

        // do we have this prop char at all?
        if (i < 0) {
            return false;
        }

        // do we have any value?
        if (this.config().propertyCharacteristics[i].value == 0) {
            return false;
        }

        switch (propCharName) {
            case PropCharNames.ACREAGE:
            case PropCharNames.UNITS:
                return true;
            case PropCharNames.SQFT:
                switch (this.config().chartType) {
                    case ActivityPanelModes.totalFMVPerSqFt:
                    case ActivityPanelModes.totalFMVPerUnit:
                        return true;
                    case ActivityPanelModes.totalFMV:
                        return false;
                    case ActivityPanelModes.landFMVPerSqFt:
                    case ActivityPanelModes.landFMVPerAcre:
                        // only if acreage is not here or acreage < 10
                        const k = this.config().propertyCharacteristics.findIndex(
                            (o) => o.label == PropCharNames.ACREAGE,
                        );
                        if (k < 0) return true;
                        return this.config().propertyCharacteristics[k].value < 10;
                }
        }

        // some other property name - just hide
        return false;
    }

    getColumnTitle(propCharName: string): string {
        switch (propCharName) {
            case PropCharNames.SQFT:
                switch (this.config().chartType) {
                    case ActivityPanelModes.totalFMVPerSqFt:
                    case ActivityPanelModes.totalFMVPerUnit:
                        return 'Total MV $/SqFt';
                    case ActivityPanelModes.totalFMV:
                        return 'error!';
                    case ActivityPanelModes.landFMVPerSqFt:
                    case ActivityPanelModes.landFMVPerAcre:
                        return 'Land MV $/SqFt';
                }
                break;

            case PropCharNames.UNITS:
                switch (this.config().chartType) {
                    case ActivityPanelModes.totalFMVPerSqFt:
                    case ActivityPanelModes.totalFMVPerUnit:
                        return 'Total MV $/Unit';
                    case ActivityPanelModes.totalFMV:
                    case ActivityPanelModes.landFMVPerSqFt:
                    case ActivityPanelModes.landFMVPerAcre:
                        return 'error!';
                }
                break;

            case PropCharNames.ACREAGE:
                switch (this.config().chartType) {
                    case ActivityPanelModes.totalFMVPerSqFt:
                    case ActivityPanelModes.totalFMVPerUnit:
                    case ActivityPanelModes.totalFMV:
                        return 'error!';
                    case ActivityPanelModes.landFMVPerSqFt:
                    case ActivityPanelModes.landFMVPerAcre:
                        return 'Land MV $/Acre';
                }
                break;
        }
    }

    getFooterTitle(propCharName: string): string {
        switch (propCharName) {
            case PropCharNames.SQFT:
                switch (this.config().chartType) {
                    case ActivityPanelModes.totalFMVPerSqFt:
                    case ActivityPanelModes.totalFMVPerUnit:
                        return 'Imps. SqFt';
                    case ActivityPanelModes.totalFMV:
                        return 'error!';
                    case ActivityPanelModes.landFMVPerSqFt:
                    case ActivityPanelModes.landFMVPerAcre:
                        return 'Land SqFt';
                }
                break;

            case PropCharNames.UNITS:
                switch (this.config().chartType) {
                    case ActivityPanelModes.totalFMVPerSqFt:
                    case ActivityPanelModes.totalFMVPerUnit:
                        return 'Units';
                    case ActivityPanelModes.totalFMV:
                    case ActivityPanelModes.landFMVPerSqFt:
                    case ActivityPanelModes.landFMVPerAcre:
                        return 'error!';
                }
                break;

            case PropCharNames.ACREAGE:
                switch (this.config().chartType) {
                    case ActivityPanelModes.totalFMVPerSqFt:
                    case ActivityPanelModes.totalFMVPerUnit:
                    case ActivityPanelModes.totalFMV:
                        return 'error!';
                    case ActivityPanelModes.landFMVPerSqFt:
                    case ActivityPanelModes.landFMVPerAcre:
                        return 'Land Acreage';
                }
                break;
        }
    }

    getPropCharValue(propCharName: string): any {
        //This was added to handle a special case. The whole panel was written assuming the values would be the rap property
        //characteristic values, but this assumption was wrong. Imps Sq Ft !== Land Sq Ft, one is a calculated value. So this
        //catches that condition and runs the calculation.
        if (
            propCharName === PropCharNames.SQFT &&
            (this.config().chartType === ActivityPanelModes.landFMVPerSqFt ||
                this.config().chartType === ActivityPanelModes.landFMVPerAcre)
        ) {
            return this.calculateLandSqFt();
        }
        const propChar = this.config().propertyCharacteristics.find((x) => x.label === propCharName);
        return propChar.value;
    }

    calculateLandSqFt() {
        //From Grant, calculation is  Land Acreage * 43,560)
        const landAcreage = this.config().propertyCharacteristics.find((x) => x.label === PropCharNames.ACREAGE);
        return parseFloat(landAcreage.value) * 43560;
    }

    async loadAllYears() {
        // we want to call api and update this.config
        if (this.parcelId()) {
            const result = await this._activityService.getDataByParcelAllYears(this.config().siteId, this.parcelId());
            this.formatData(result);
        } else {
            const result = await this._activityService.getDataForAllYears(
                this.config().siteId,
                this.config().parcelFilterList,
            );
            this.formatData(result);
        }
    }

    formatData(result) {
        console.log('table directive - activityService.getDataForAllYears - result', result);

        // convert received data to the right format
        // we don't want to replace

        // update original config
        this.originalConfig().annualData = result.annualData;
        this.originalConfig().propertyCharacteristics = result.propertyCharacteristics;

        const convertedConfig = this._activityService.createGraphConfig(result, this.config().chartType);

        this.config().series = convertedConfig.series;
        this.config().xAxis = convertedConfig.xAxis;
        this.config().useLatestActualized = this.originalConfig().useLatestActualized;
        this.config().loadedAllYears = true;
        this.config().availableYears = this.originalConfig().availableYears;
        this.originalConfig().loadedAllYears = true;
    }

    getPrecision(fieldName: string): string {
        if (fieldName == ActivityPanelModes.totalFMV && this.config().chartType != ActivityPanelModes.totalTaxes)
            return '1.0-0';

        switch (this.config().chartType) {
            case ActivityPanelModes.totalFMV:
                return '1.0-0';
            case ActivityPanelModes.totalFMVPerSqFt:
            case ActivityPanelModes.totalFMVPerUnit:
            case ActivityPanelModes.landFMVPerSqFt:
            case ActivityPanelModes.landFMVPerAcre:
                return '1.2-2';
            case ActivityPanelModes.totalTaxes:
            case 'recentAppeals':
                return '1.2-2';
        }
        return '1.0-0';
    }

    getAppealCellValue(year: number, fieldName: string) {
        const annualYearIndex = this.config().xAxis.categories.indexOf(year);
        const fieldValue = this.config().series[1].data[annualYearIndex][fieldName];

        if (!fieldValue) return '';

        // is this a number? if so, format and return it
        if (isFinite(fieldValue)) {
            return this._currencyPipe.transform(fieldValue, 'USD', fieldName === 'savings' ? '$' : '', `1.0-0`);
        }
        return fieldValue;
    }
}
